import { Button, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@parcel/ui';
import { IdCard, Loader2, Mail, PhoneIncoming } from 'lucide-react';
import type * as React from 'react';

export type RevealIconKind = 'email' | 'phone' | 'profile';

const ICONS = { email: Mail, phone: PhoneIncoming, profile: IdCard } as const;

/** The kind's icon, with a small green check when Parcel already holds the value, or a red cross when a lookup found none. */
export function RevealIcon({
  kind,
  mark,
}: {
  kind: RevealIconKind;
  mark?: 'held' | 'missed' | null;
}) {
  const Icon = ICONS[kind];
  return (
    <span className="relative inline-flex shrink-0" data-reveal-mark={mark ?? 'none'}>
      <Icon className="size-4 text-muted-foreground" aria-hidden />
      {mark ? (
        <svg
          viewBox="0 0 10 10"
          // !important: inside a Button, [&_svg]:size-4 would inflate this 10px badge to the icon's size.
          className="absolute -right-1 -top-1 !size-2.5 rounded-full ring-[1.5px] ring-background"
          aria-hidden="true"
        >
          <circle
            cx="5"
            cy="5"
            r="5"
            className={mark === 'held' ? 'fill-emerald-500' : 'fill-red-500'}
          />
          {mark === 'held' ? (
            <path
              d="M2.9 5.1 4.3 6.5 7.1 3.6"
              fill="none"
              stroke="white"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ) : (
            <path
              d="M3.4 3.4 6.6 6.6M6.6 3.4 3.4 6.6"
              fill="none"
              stroke="white"
              strokeWidth="1.3"
              strokeLinecap="round"
            />
          )}
        </svg>
      ) : null}
    </span>
  );
}

export function Tip({ tip, children }: { tip: string; children: React.ReactNode }) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent>{tip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** One click reveals; the tooltip states the cost first. A disabled button keeps its tooltip. */
export function RevealButton({
  kind,
  label,
  tip,
  held = false,
  pending = false,
  disabled = false,
  onReveal,
}: {
  kind: RevealIconKind;
  label: string;
  tip: string;
  held?: boolean;
  pending?: boolean;
  disabled?: boolean;
  onReveal?: () => void;
}) {
  const button = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="h-8 gap-2 px-2.5"
      aria-description={tip}
      disabled={disabled || pending}
      onClick={(event) => {
        event.stopPropagation();
        onReveal?.();
      }}
    >
      {pending ? (
        <Loader2 className="size-4 animate-spin" aria-hidden />
      ) : (
        <RevealIcon kind={kind} mark={held ? 'held' : null} />
      )}
      {label}
    </Button>
  );
  // A disabled button takes no pointer events, so its tooltip hangs on a wrapper.
  return <Tip tip={tip}>{disabled ? <span className="inline-flex">{button}</span> : button}</Tip>;
}

/** A lookup that found nothing: the kind's icon with a red cross, and when it was checked on hover. */
export function RevealMissed({
  kind,
  label,
  tip,
}: {
  kind: RevealIconKind;
  label: string;
  tip: string;
}) {
  return (
    <Tip tip={tip}>
      <span
        className="inline-flex items-center gap-2 text-sm text-muted-foreground"
        data-testid={`reveal-missed-${kind}`}
      >
        <RevealIcon kind={kind} mark="missed" />
        {label}
      </span>
    </Tip>
  );
}
